import { useState } from 'react';
import { motion } from 'framer-motion';
import { adminRHData } from '../data/adminRHData';
import AikupCard from '../components/AikupCard';
import AikupModal from '../components/AikupModal';
import ExpertVisionModal from '../components/ExpertVisionModal';
import CadreReferentielModal from '../components/CadreReferentielModal';
import AméliorationContinueModal from '../components/AméliorationContinueModal';
import GestionRecevablesModal from '../components/GestionRecevablesModal';
import DemandeCreditsModal from '../components/DemandeCreditsModal';
import AIInsightBubble from '../components/AIInsightBubble';
import Header from '../layout/Header';
import LeadersSection from '../components/LeadersSection';
import FormationGestionDocumentaireCard from '../components/FormationGestionDocumentaireCard';
import ArbrePRIsMECard from '../components/ArbrePRIsMECard';
import AdminRHReunionsModal from '../components/AdminRHReunionsModal';
import AdminRHRACIModal from '../components/AdminRHRACIModal';
import AdminRHReferentielModal from '../components/AdminRHReferentielModal';
import GouvernanceContratsBZModal from '../components/GouvernanceContratsBZModal';
import AdminRHAssurancesModal from '../components/AdminRHAssurancesModal';
import LitigeClientModal from '../components/LitigeClientModal';
import PolitiqueTeletravailBZModal from '../components/PolitiqueTeletravailBZModal';
import PolitiqueCartesCreditComptesDepensesBZModal from '../components/PolitiqueCartesCreditComptesDepensesBZModal';
import PolitiqueDeplacementsUSAModal from '../components/PolitiqueDeplacementsUSAModal';
import TactiCERHModal from '../components/TactiCERHModal';
import { Navigation, Lock, Briefcase, Target, Users, Calendar, Plane, ShieldCheck, FileCheck2, CreditCard, Receipt } from 'lucide-react';
import type { AdminRHItem } from '../data/adminRHData';

export default function AdminRH() {
  const [selectedItem, setSelectedItem] = useState<AdminRHItem | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [expertVisionModal, setExpertVisionModal] = useState<{
    isOpen: boolean;
    expert: 'dany' | 'yannick' | 'francois' | null;
    item: AdminRHItem | null;
  }>({
    isOpen: false,
    expert: null,
    item: null,
  });
  const [isCadreReferentielOpen, setIsCadreReferentielOpen] = useState(false);
  const [isAméliorationOpen, setIsAméliorationOpen] = useState(false);
  const [isGestionRecevablesOpen, setIsGestionRecevablesOpen] = useState(false);
  const [isDemandeCreditsOpen, setIsDemandeCreditsOpen] = useState(false);
  const [isReunionsGouvernanceOpen, setIsReunionsGouvernanceOpen] = useState(false);
  const [isRACIOfficielOpen, setIsRACIOfficielOpen] = useState(false);
  const [isReferentielAdminOpen, setIsReferentielAdminOpen] = useState(false);
  const [isTactiCERHModalOpen, setIsTactiCERHModalOpen] = useState(false);
  const [isGouvernanceContratsOpen, setIsGouvernanceContratsOpen] = useState(false);
  const [isAssurancesOpen, setIsAssurancesOpen] = useState(false);
  const [isLitigeClientOpen, setIsLitigeClientOpen] = useState(false);
  const [isPolitiqueTeletravailOpen, setIsPolitiqueTeletravailOpen] = useState(false);
  const [isPolitiqueCartesCreditOpen, setIsPolitiqueCartesCreditOpen] = useState(false);
  const [isPolitiqueDeplacementsUSAOpen, setIsPolitiqueDeplacementsUSAOpen] = useState(false);

  const handleCardClick = (item: AdminRHItem) => {
    if (item.id === 1) {
      setIsReferentielAdminOpen(true);
    } else if (item.id === 3) {
      setIsRACIOfficielOpen(true);
    } else if (item.id === 6) {
      // Si c'est la carte "Cadre Référentiel BZ"
      setIsCadreReferentielOpen(true);
    } else if (item.id === 10) {
      // Si c'est la carte "Amélioration Continue et Rigueur"
      setIsAméliorationOpen(true);
    } else if (item.id === 19) {
      // Si c'est la carte "Gestion des Recevables"
      setIsGestionRecevablesOpen(true);
    } else if (item.id === 20) {
      // Si c'est la carte "Demande de Crédits"
      setIsDemandeCreditsOpen(true);
    } else if (item.id === 21) {
      setIsAssurancesOpen(true);
    } else {
      setSelectedItem(item);
      setIsModalOpen(true);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setTimeout(() => setSelectedItem(null), 300);
  };

  const leaders = [
    {
      name: "François Delisle",
      title: "VP Gouvernance corporative",
      avatar: "/FP.png",
      vision: "François est le gardien de la gouvernance corporative : finance, risques, conformité, contrôles et décisions structurantes. Sa présence dans Administration & RH rend clair que ce département relève d’une gouvernance haute direction, pas seulement d’une gestion administrative quotidienne.",
      attentes: "François s'attend à ce que PRIsME donne une vue simple des responsabilités, contrôles et risques. Il veut que les décisions administratives et RH soient traçables, utiles et alignées avec la valeur long terme de BZ.",
      pagePRIsME: "La page Administration & RH devient le cockpit de gouvernance corporative : François porte la hauteur, Mélissa la qualité comptable, LAH l’humain et les opérations administratives."
    },
    {
      name: "Mélissa Pelletier",
      title: "Cheffe spécialiste comptabilité BZ",
      avatar: "/MP.png",
      vision: "Mélissa est la référence comptable la plus forte chez BZ. Rien ne lui échappe : chiffres, suivis, contrôles, rigueur et sens humain. Elle voit PRIsME comme l’outil qui structure la qualité comptable et protège les décisions financières.",
      attentes: "Mélissa s'attend à ce que PRIsME reflète notre rigueur comptable et notre attention aux détails. Elle veut voir chaque processus documenté, chaque contrôle en place, chaque valeur respectée.",
      pagePRIsME: "Administration & RH doit rendre la comptabilité claire, fiable et utile au CE, sans noyer les équipes dans la lourdeur."
    },
    {
      name: "Louis-Alexandre Hébert",
      title: "Directeur RH & opérations administratives",
      avatar: "/LAH.jpg",
      vision: "LAH porte l’humain, les politiques RH et les opérations administratives. Il aide BZ à garder les règles simples, les rôles clairs et l’accompagnement concret quand l’organisation change.",
      attentes: "LAH s'attend à ce que PRIsME soutienne les gestionnaires : politiques claires, responsabilités explicites, suivi humain et moins de zones grises administratives.",
      pagePRIsME: "La page PRIsME d'Administration & RH représente le point d’ancrage de LAH pour relier RH, opérations admin et gouvernance dans une même lecture simple."
    }
  ];

  // Organiser par catégorie
  const categories = Array.from(new Set(adminRHData.map(item => item.category)));

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <Header 
        title="Administration & RH" 
        subtitle="Les piliers de l'excellence humaine & opérationnelle pour une organisation durable"
      />
      
      <div className="max-w-7xl mx-auto px-8 pb-12">
        <LeadersSection leaders={leaders} title="Nos Leaders - Administration & RH" />
        
        {/* Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-xl shadow-md p-6 border-l-4 border-teal-500"
          >
            <p className="text-sm text-gray-600 mb-1">Outils Disponibles</p>
            <p className="text-3xl font-bold text-gray-900">{adminRHData.length}</p>
            <p className="text-xs text-gray-500 mt-1">Frameworks admin</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-xl shadow-md p-6 border-l-4 border-green-500"
          >
            <p className="text-sm text-gray-600 mb-1">Score Maturité</p>
            <p className="text-3xl font-bold text-gray-900">
              {Math.round(adminRHData.reduce((sum, item) => sum + (item.kpiScore || 0), 0) / adminRHData.length)}%
            </p>
            <p className="text-xs text-gray-500 mt-1">Moyenne excellence</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-xl shadow-md p-6 border-l-4 border-blue-500"
          >
            <p className="text-sm text-gray-600 mb-1">Catégories</p>
            <p className="text-3xl font-bold text-gray-900">{categories.length}</p>
            <p className="text-xs text-gray-500 mt-1">Domaines couverts</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white rounded-xl shadow-md p-6 border-l-4 border-emerald-500"
          >
            <p className="text-sm text-gray-600 mb-1">Gouvernance</p>
            <p className="text-3xl font-bold text-gray-900">
              {adminRHData.filter(item => item.category === 'Gouvernance' || item.category === 'Cadre Référentiel').length}
            </p>
            <p className="text-xs text-gray-500 mt-1">Outils gouvernance</p>
          </motion.div>
        </div>

        {/* Cards par catégorie */}
        {categories.map((category, categoryIndex) => {
          const itemsInCategory = adminRHData.filter(item => item.category === category);
          
          return (
            <div key={category} className="mb-12">
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: categoryIndex * 0.1 }}
                className="flex items-center gap-3 mb-6"
              >
                <div className="flex-1 h-px bg-gradient-to-r from-teal-500 to-emerald-500"></div>
                <h2 className="text-2xl font-bold text-gray-800 px-4">{category}</h2>
                <div className="flex-1 h-px bg-gradient-to-l from-teal-500 to-emerald-500"></div>
              </motion.div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {itemsInCategory.map((item, idx) => (
                  <motion.div
                    key={item.id}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: categoryIndex * 0.1 + idx * 0.05 }}
                    className="relative"
                  >
                    {item.id === 19 && (
                      <div className="absolute -top-3 -right-3 z-10 bg-gradient-to-r from-red-600 to-red-700 text-white px-4 py-2 rounded-full shadow-2xl transform rotate-12 animate-pulse">
                        <div className="flex items-center gap-2">
                          <span className="text-2xl font-black">⚡</span>
                          <span className="font-bold text-sm">PRIORITÉ 1</span>
                        </div>
                      </div>
                    )}
                    <AikupCard
                      item={item}
                      onClick={() => handleCardClick(item)}
                      onExpertClick={(expert: 'dany' | 'yannick' | 'francois') => {
                        setExpertVisionModal({
                          isOpen: true,
                          expert,
                          item,
                        });
                      }}
                      index={idx}
                      customLeaders={
                        item.id === 19 ? [
                          { name: 'Dany', avatar: '/DB.png', title: 'Dany - Amélioration continue & innovation' },
                          { name: 'Louis-Alexandre Hébert', avatar: '/LAH.jpg', title: 'LAH - Directeur RH & opérations administratives' }
                        ] : undefined
                      }
                    />
                  </motion.div>
                ))}

                {category === 'Gouvernance' && (
                  <>
                    <motion.button
                      type="button"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="relative h-64 w-full self-start overflow-hidden rounded-lg bg-gradient-to-br from-slate-900 via-teal-900 to-slate-800 shadow-md hover:shadow-xl transition-all duration-300 text-left group focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-600 focus-visible:ring-offset-2"
                      onClick={() => setIsLitigeClientOpen(true)}
                      aria-haspopup="dialog"
                    >
                      <ShieldCheck className="absolute -right-5 -bottom-5 h-48 w-48 text-teal-300/10 transition-transform duration-300 group-hover:scale-110" aria-hidden="true" />
                      <span className="absolute top-2 left-2 rounded-full border border-white/30 bg-white/20 px-3 py-1 text-xs font-medium text-white backdrop-blur-sm">
                        Tous les départements
                      </span>
                      <span className="relative flex h-full flex-col p-6 pt-16 text-white">
                        <span className="text-xl font-bold leading-snug break-words">Litige client — premiers réflexes</span>
                        <span className="mt-3 text-sm leading-relaxed text-teal-100">Les bons réflexes pour communiquer, documenter et coordonner.</span>
                        <span className="mt-auto pt-3 text-xs font-semibold text-teal-200">Consulter l’aide-mémoire →</span>
                      </span>
                      <span className="sr-only">
                        Aide-mémoire interne : communications avec le client, précautions liées aux assurances, documentation des faits et coordination du dossier.
                      </span>
                    </motion.button>

                    {/* Card Réunions de gouvernance */}
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: categoryIndex * 0.1 + itemsInCategory.length * 0.05 + 0.1 }}
                      className="bg-white rounded-xl shadow-lg p-8 border-2 border-teal-200 hover:border-teal-400 transition-all cursor-pointer"
                      onClick={() => setIsReunionsGouvernanceOpen(true)}
                    >
                      <div className="flex items-start justify-between mb-6">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-3">
                            <div className="bg-gradient-to-r from-teal-500 to-emerald-600 p-3 rounded-xl">
                              <Navigation className="w-8 h-8 text-white" />
                            </div>
                            <div>
                              <h3 className="text-2xl font-bold text-gray-900">🧭 Réunions de gouvernance – Administration & RH</h3>
                              <p className="text-sm text-gray-600 mt-1">Centralisation des réunions de gouvernance</p>
                            </div>
                          </div>
                          <p className="text-gray-700 text-lg leading-relaxed mb-4">
                            Cette card centralise les réunions de gouvernance du département Administration & RH.
                            Elle vise à clarifier les rôles et responsabilités, assurer le respect des cadres et procédures,
                            documenter les décisions structurantes, et maintenir une gouvernance continue et cohérente.
                          </p>
                          
                          {/* Parties prenantes */}
                          <div className="mb-4">
                            <p className="text-sm font-semibold text-gray-700 mb-2">Parties prenantes :</p>
                            <div className="flex items-center gap-2 flex-wrap">
                              <img src="/FP.png" alt="François Delisle" className="w-10 h-10 rounded-full border-2 border-teal-200 hover:border-teal-400 transition-colors" title="François Delisle - VP Gouvernance corporative" />
                              <img src="/MP.png" alt="Mélissa" className="w-10 h-10 rounded-full border-2 border-teal-200 hover:border-teal-400 transition-colors" title="Mélissa - Cheffe spécialiste comptabilité BZ" />
                              <img src="/LAH.jpg" alt="Louis-Alexandre Hébert" className="w-10 h-10 rounded-full border-2 border-teal-200 hover:border-teal-400 transition-colors" title="LAH - Directeur RH & opérations administratives" />
                              <img src="/RD.png" alt="Richard Delisle" className="w-10 h-10 rounded-full border-2 border-teal-200 hover:border-teal-400 transition-colors" title="Richard Delisle - Directeur Cloud & Licences" />
                              <img src="/DB.png" alt="Dany" className="w-10 h-10 rounded-full border-2 border-teal-200 hover:border-teal-400 transition-colors" title="LAH - Directeur RH & opérations administratives" />
                              <img src="/DB.png" alt="Dany" className="w-10 h-10 rounded-full border-2 border-teal-200 hover:border-teal-400 transition-colors" title="Dany - Amélioration continue & innovation" />
                            </div>
                          </div>

                          <div className="flex items-center gap-6 text-gray-600">
                            <div className="flex items-center gap-2">
                              <Navigation className="w-5 h-5" />
                              <span>Gouvernance structurée</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Lock className="w-5 h-5" />
                              <span>Décisions documentées</span>
                            </div>
                          </div>
                        </div>
                        <div className="ml-6">
                          <span className="px-4 py-2 rounded-full text-sm font-semibold bg-teal-100 text-teal-700">
                            Gouvernance
                          </span>
                        </div>
                      </div>
                    </motion.div>

                    {/* Card RACI officiel */}
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: categoryIndex * 0.1 + itemsInCategory.length * 0.05 + 0.2 }}
                      className="bg-white rounded-xl shadow-lg p-8 border-2 border-emerald-200 hover:border-emerald-400 transition-all cursor-pointer relative"
                      onClick={() => setIsRACIOfficielOpen(true)}
                    >
                      <div className="absolute -top-3 -right-3 z-10 bg-gradient-to-r from-red-600 to-red-700 text-white px-4 py-2 rounded-full shadow-2xl transform rotate-12 animate-pulse">
                        <div className="flex items-center gap-2">
                          <span className="text-2xl font-black">⚡</span>
                          <span className="font-bold text-sm">PRIORITÉ 1</span>
                        </div>
                      </div>
                      
                      {/* Avatars en haut à droite */}
                      <div className="absolute top-4 right-4 z-10 flex gap-2">
                        <img 
                          src="/FP.png" 
                          alt="François Delisle" 
                          className="w-10 h-10 rounded-full border-2 border-emerald-200 hover:border-emerald-400 transition-colors shadow-lg" 
                          title="François Delisle - VP Gouvernance corporative"
                          onClick={(e) => e.stopPropagation()}
                        />
                        <img 
                          src="/DB.png" 
                          alt="Dany" 
                          className="w-10 h-10 rounded-full border-2 border-emerald-200 hover:border-emerald-400 transition-colors shadow-lg" 
                          title="Dany - Amélioration continue & innovation"
                          onClick={(e) => e.stopPropagation()}
                        />
                      </div>
                      
                      <div className="flex items-start justify-between mb-6">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-3">
                            <div className="bg-gradient-to-r from-emerald-500 to-green-600 p-3 rounded-xl">
                              <Lock className="w-8 h-8 text-white" />
                            </div>
                            <div>
                              <h3 className="text-2xl font-bold text-gray-900">🔐 RACI officiel – Administration & RH</h3>
                              <p className="text-sm text-gray-600 mt-1">Référence unique pour les rôles et responsabilités</p>
                            </div>
                          </div>
                          <p className="text-gray-700 text-lg leading-relaxed mb-4">
                            Cette page définit clairement et officiellement les rôles et responsabilités en gouvernance, finance, RH,
                            opérations administratives et outils. Elle sert de référence unique, base de responsabilisation,
                            et point d'ancrage pour les décisions et audits.
                          </p>
                          
                          {/* Parties prenantes */}
                          <div className="mb-4">
                            <p className="text-sm font-semibold text-gray-700 mb-2">Parties prenantes :</p>
                            <div className="flex items-center gap-2 flex-wrap">
                              <img src="/FP.png" alt="François Delisle" className="w-10 h-10 rounded-full border-2 border-emerald-200 hover:border-emerald-400 transition-colors" title="François Delisle - VP Gouvernance corporative" />
                              <img src="/MP.png" alt="Mélissa" className="w-10 h-10 rounded-full border-2 border-emerald-200 hover:border-emerald-400 transition-colors" title="Mélissa - Cheffe spécialiste comptabilité BZ" />
                              <img src="/LAH.jpg" alt="Louis-Alexandre Hébert" className="w-10 h-10 rounded-full border-2 border-emerald-200 hover:border-emerald-400 transition-colors" title="LAH - Directeur RH & opérations administratives" />
                              <img src="/RD.png" alt="Richard Delisle" className="w-10 h-10 rounded-full border-2 border-emerald-200 hover:border-emerald-400 transition-colors" title="Richard Delisle - Directeur Cloud & Licences" />
                              <img src="/DB.png" alt="Dany" className="w-10 h-10 rounded-full border-2 border-emerald-200 hover:border-emerald-400 transition-colors" title="LAH - Directeur RH & opérations administratives" />
                              <img src="/DB.png" alt="Dany" className="w-10 h-10 rounded-full border-2 border-emerald-200 hover:border-emerald-400 transition-colors" title="Dany - Amélioration continue & innovation" />
                            </div>
                          </div>

                          <div className="flex items-center gap-6 text-gray-600">
                            <div className="flex items-center gap-2">
                              <Lock className="w-5 h-5" />
                              <span>Référence officielle</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Navigation className="w-5 h-5" />
                              <span>6 domaines couverts</span>
                            </div>
                          </div>
                        </div>
                        <div className="ml-6">
                          <span className="px-4 py-2 rounded-full text-sm font-semibold bg-emerald-100 text-emerald-700">
                            RACI
                          </span>
                        </div>
                      </div>
                    </motion.div>

                    {/* Card Gouvernance - Gestion des contrats BZ */}
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: categoryIndex * 0.1 + itemsInCategory.length * 0.05 + 0.3 }}
                      className="bg-white rounded-xl shadow-lg p-8 border-2 border-amber-200 hover:border-amber-400 transition-all cursor-pointer"
                      onClick={() => setIsGouvernanceContratsOpen(true)}
                    >
                      <div className="flex items-start justify-between mb-6">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-3">
                            <div className="bg-gradient-to-r from-amber-500 to-orange-600 p-3 rounded-xl">
                              <Briefcase className="w-8 h-8 text-white" />
                            </div>
                            <div>
                              <h3 className="text-2xl font-bold text-gray-900">
                                📜 Gouvernance – Gestion des contrats BZ
                              </h3>
                              <p className="text-sm text-gray-600 mt-1">
                                Cadre de gouvernance pour centraliser la gestion et le suivi des contrats BZ.
                              </p>
                            </div>
                          </div>
                          <p className="text-gray-700 text-lg leading-relaxed mb-4">
                            Cette card deviendra la vue centrale pour les contrats BZ&nbsp;: responsabilités,
                            décisions, renouvellements et alignement avec les cadres PRIsME. Pour l’instant, elle
                            réutilise la gouvernance PRIsME telle quelle; nous personnaliserons onglet par onglet.
                          </p>

                          <div className="mb-4">
                            <p className="text-sm font-semibold text-gray-700 mb-2">Parties prenantes :</p>
                            <div className="flex items-center gap-2 flex-wrap">
                              <img
                                src="/MP.png"
                                alt="Mélissa"
                                className="w-10 h-10 rounded-full border-2 border-amber-200 hover:border-amber-400 transition-colors"
                                title="Mélissa - Cheffe spécialiste comptabilité BZ"
                              />
                              <img
                                src="/LAH.jpg"
                                alt="Louis-Alexandre Hébert"
                                className="w-10 h-10 rounded-full border-2 border-amber-200 hover:border-amber-400 transition-colors"
                                title="LAH - Directeur RH & opérations administratives"
                              />
                              <img
                                src="/RD.png"
                                alt="Richard Delisle"
                                className="w-10 h-10 rounded-full border-2 border-amber-200 hover:border-amber-400 transition-colors"
                                title="Richard Delisle - Directeur Cloud & Licences"
                              />
                              <img
                                src="/FP.png"
                                alt="François Delisle"
                                className="w-10 h-10 rounded-full border-2 border-amber-200 hover:border-amber-400 transition-colors"
                                title="François Delisle - VP Gouvernance corporative"
                              />
                              <img
                                src="/DB.png"
                                alt="Dany"
                                className="w-10 h-10 rounded-full border-2 border-amber-200 hover:border-amber-400 transition-colors"
                                title="Dany - Amélioration continue & innovation"
                              />
                            </div>
                          </div>

                          <div className="flex items-center gap-6 text-gray-600">
                            <div className="flex items-center gap-2">
                              <Briefcase className="w-5 h-5" />
                              <span>Contrats & licences</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Target className="w-5 h-5" />
                              <span>Décisions traçables</span>
                            </div>
                          </div>
                        </div>
                        <div className="ml-6">
                          <span className="px-4 py-2 rounded-full text-sm font-semibold bg-amber-100 text-amber-700">
                            Contrats BZ
                          </span>
                        </div>
                      </div>
                    </motion.div>

                    {/* Card Politique télétravail BZ */}
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: categoryIndex * 0.1 + itemsInCategory.length * 0.05 + 0.35 }}
                      className="bg-white rounded-xl shadow-lg p-8 border-2 border-cyan-200 hover:border-cyan-400 transition-all cursor-pointer"
                      onClick={() => setIsPolitiqueTeletravailOpen(true)}
                    >
                      <div className="flex items-start justify-between mb-6">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-3">
                            <div className="bg-gradient-to-r from-cyan-500 to-blue-700 p-3 rounded-xl">
                              <ShieldCheck className="w-8 h-8 text-white" />
                            </div>
                            <div>
                              <h3 className="text-2xl font-bold text-gray-900">
                                🏠 Politique télétravail BZ
                              </h3>
                              <p className="text-sm text-gray-600 mt-1">
                                Politique RH en vigueur pour autoriser, maintenir, réviser ou retirer le télétravail.
                              </p>
                            </div>
                          </div>
                          <p className="text-gray-700 text-lg leading-relaxed mb-4">
                            Cette card centralise la politique simplifiée de télétravail BZ&nbsp;: admissibilité,
                            équité interne, règle générale d’une journée par semaine, responsabilités, confidentialité et
                            lien futur avec le Cahier des politiques. Elle est maintenant en vigueur dans PRIsME.
                          </p>

                          <div className="mb-4">
                            <p className="text-sm font-semibold text-gray-700 mb-2">Parties prenantes :</p>
                            <div className="flex items-center gap-2 flex-wrap">
                              <img
                                src="/DB.png"
                                alt="Dany"
                                className="w-10 h-10 rounded-full border-2 border-cyan-200 hover:border-cyan-400 transition-colors"
                                title="Dany - RH / amélioration continue"
                              />
                              <img
                                src="/MP.png"
                                alt="Mélissa"
                                className="w-10 h-10 rounded-full border-2 border-cyan-200 hover:border-cyan-400 transition-colors"
                                title="Mélissa - Comptabilité BZ"
                              />
                              <img
                                src="/LAH.jpg"
                                alt="Louis-Alexandre Hébert"
                                className="w-10 h-10 rounded-full border-2 border-cyan-200 hover:border-cyan-400 transition-colors"
                                title="LAH - Opérations administratives & RH"
                              />
                              <img
                                src="/FP.png"
                                alt="François Delisle"
                                className="w-10 h-10 rounded-full border-2 border-cyan-200 hover:border-cyan-400 transition-colors"
                                title="François Delisle - VP Gouvernance corporative"
                              />
                            </div>
                          </div>

                          <div className="flex items-center gap-6 text-gray-600">
                            <div className="flex items-center gap-2">
                              <FileCheck2 className="w-5 h-5" />
                              <span>One-pager v6 intégré</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Users className="w-5 h-5" />
                              <span>Employés BZ</span>
                            </div>
                          </div>
                        </div>
                        <div className="ml-6">
                          <span className="px-4 py-2 rounded-full text-sm font-semibold bg-cyan-100 text-cyan-700">
                            En vigueur
                          </span>
                        </div>
                      </div>
                    </motion.div>

                    {/* Card Politique cartes de crédit et comptes de dépenses BZ */}
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: categoryIndex * 0.1 + itemsInCategory.length * 0.05 + 0.37 }}
                      className="bg-white rounded-xl shadow-lg p-8 border-2 border-yellow-200 hover:border-yellow-400 transition-all cursor-pointer"
                      onClick={() => setIsPolitiqueCartesCreditOpen(true)}
                    >
                      <div className="flex items-start justify-between mb-6">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-3">
                            <div className="bg-gradient-to-r from-yellow-500 to-amber-700 p-3 rounded-xl">
                              <Receipt className="w-8 h-8 text-white" />
                            </div>
                            <div>
                              <h3 className="text-2xl font-bold text-gray-900">
                                💳 Cartes de crédit et comptes de dépenses BZ
                              </h3>
                              <p className="text-sm text-gray-600 mt-1">
                                Politique RH en vigueur pour encadrer les dépenses, factures et validations BZDepenses.
                              </p>
                            </div>
                          </div>
                          <p className="text-gray-700 text-lg leading-relaxed mb-4">
                            Cette card centralise la politique simplifiée V14 sur les cartes de crédit d’entreprise,
                            les comptes de dépenses, les pièces justificatives, les codes de grand livre, les échéances
                            mensuelles et les responsabilités employé / directeur / administration.
                          </p>

                          <div className="mb-4">
                            <p className="text-sm font-semibold text-gray-700 mb-2">Parties prenantes :</p>
                            <div className="flex items-center gap-2 flex-wrap">
                              <img
                                src="/DB.png"
                                alt="Dany"
                                className="w-10 h-10 rounded-full border-2 border-yellow-200 hover:border-yellow-400 transition-colors"
                                title="Dany - RH / amélioration continue"
                              />
                              <img
                                src="/MP.png"
                                alt="Mélissa"
                                className="w-10 h-10 rounded-full border-2 border-yellow-200 hover:border-yellow-400 transition-colors"
                                title="Mélissa - Comptabilité BZ"
                              />
                              <img
                                src="/LAH.jpg"
                                alt="Louis-Alexandre Hébert"
                                className="w-10 h-10 rounded-full border-2 border-yellow-200 hover:border-yellow-400 transition-colors"
                                title="LAH - Opérations administratives & RH"
                              />
                              <img
                                src="/FP.png"
                                alt="François Delisle"
                                className="w-10 h-10 rounded-full border-2 border-yellow-200 hover:border-yellow-400 transition-colors"
                                title="François Delisle - VP Gouvernance corporative"
                              />
                            </div>
                          </div>

                          <div className="flex flex-wrap items-center gap-6 text-gray-600">
                            <div className="flex items-center gap-2">
                              <FileCheck2 className="w-5 h-5" />
                              <span>PDF V14 intégré</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <CreditCard className="w-5 h-5" />
                              <span>BZDepenses</span>
                            </div>
                          </div>
                        </div>
                        <div className="ml-6">
                          <span className="px-4 py-2 rounded-full text-sm font-semibold bg-yellow-100 text-yellow-700">
                            En vigueur
                          </span>
                        </div>
                      </div>
                    </motion.div>

                    {/* Card Politique déplacements USA */}
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: categoryIndex * 0.1 + itemsInCategory.length * 0.05 + 0.4 }}
                      className="bg-white rounded-xl shadow-lg p-8 border-2 border-sky-200 hover:border-sky-400 transition-all cursor-pointer relative overflow-hidden"
                      onClick={() => setIsPolitiqueDeplacementsUSAOpen(true)}
                    >
                      <div className="absolute -top-3 -right-3 z-10 bg-gradient-to-r from-sky-600 to-blue-700 text-white px-4 py-2 rounded-full shadow-2xl transform rotate-12">
                        <div className="flex items-center gap-2">
                          <span className="text-xl font-black">RAG</span>
                          <span className="font-bold text-sm">BZ</span>
                        </div>
                      </div>

                      <div className="flex items-start justify-between mb-6">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-3">
                            <div className="bg-gradient-to-r from-sky-500 to-blue-700 p-3 rounded-xl">
                              <Plane className="w-8 h-8 text-white" />
                            </div>
                            <div>
                              <h3 className="text-2xl font-bold text-gray-900">
                                🛂 Politique déplacements USA — douanes & immigration
                              </h3>
                              <p className="text-sm text-gray-600 mt-1">
                                Référence RAG BZ pour prévenir les refus d’entrée et cadrer les mandats américains.
                              </p>
                            </div>
                          </div>
                          <p className="text-gray-700 text-lg leading-relaxed mb-4">
                            Cette card documente la politique future de BZ pour les déplacements aux États-Unis :
                            validation avant départ, activités permises, red flags, dossier de voyage, RACI et preuves à
                            conserver. Elle ne documente aucun cas individuel.
                          </p>

                          <div className="mb-4">
                            <p className="text-sm font-semibold text-gray-700 mb-2">Parties prenantes :</p>
                            <div className="flex items-center gap-2 flex-wrap">
                              <img src="/DB.png" alt="Dany" className="w-10 h-10 rounded-full border-2 border-sky-200 hover:border-sky-400 transition-colors" title="Dany - Direction" />
                              <img src="/LAH.jpg" alt="Louis-Alexandre Hébert" className="w-10 h-10 rounded-full border-2 border-sky-200 hover:border-sky-400 transition-colors" title="LAH - Opérations administratives & RH" />
                              <img src="/FP.png" alt="François Delisle" className="w-10 h-10 rounded-full border-2 border-sky-200 hover:border-sky-400 transition-colors" title="François Delisle - VP Gouvernance corporative" />
                              <img src="/YM.png" alt="Yannick" className="w-10 h-10 rounded-full border-2 border-sky-200 hover:border-sky-400 transition-colors" title="Yannick - RH" />
                            </div>
                          </div>

                          <div className="flex flex-wrap items-center gap-6 text-gray-600">
                            <div className="flex items-center gap-2">
                              <ShieldCheck className="w-5 h-5" />
                              <span>Validation pré-départ</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <FileCheck2 className="w-5 h-5" />
                              <span>Dossier traçable</span>
                            </div>
                          </div>
                        </div>
                        <div className="ml-6">
                          <span className="px-4 py-2 rounded-full text-sm font-semibold bg-sky-100 text-sky-700">
                            Immigration USA
                          </span>
                        </div>
                      </div>
                    </motion.div>
                  </>
                )}
              </div>
            </div>
          );
        })}

        {/* Section Outils */}
        <div className="mb-12">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="flex items-center gap-3 mb-6"
          >
            <div className="flex-1 h-px bg-gradient-to-r from-rose-500 to-teal-500"></div>
            <h2 className="text-2xl font-bold text-gray-800 px-4">Outils</h2>
            <div className="flex-1 h-px bg-gradient-to-l from-rose-500 to-teal-500"></div>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Card TactiCE RH */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              whileHover={{ scale: 1.02, y: -5 }}
              className="relative overflow-hidden rounded-3xl shadow-lg hover:shadow-xl transition-all duration-300 cursor-pointer h-80 group"
              onClick={() => setIsTactiCERHModalOpen(true)}
            >
              <div
                className="absolute inset-0 bg-cover bg-center transition-transform duration-500 group-hover:scale-110"
                style={{
                  backgroundImage: 'url(https://images.unsplash.com/photo-1573164574572-cb89e39749b4?w=1200&auto=format&fit=crop&q=80)',
                }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/60 to-black/30 group-hover:from-rose-900/95 group-hover:via-teal-900/80 transition-all duration-300" />

              <div className="relative h-full p-6 flex flex-col justify-between text-white">
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <Briefcase className="w-6 h-6 text-rose-300" />
                    <span className="px-3 py-1 backdrop-blur-sm bg-rose-500/40 border border-rose-400/60 rounded-full text-xs font-medium">
                      Outil RH
                    </span>
                  </div>
                  <h3 className="text-2xl font-bold mb-2">TactiCE RH</h3>
                  <p className="text-rose-100 text-sm mb-4">
                    Documentation holistique du Hub RH : esprit, gouvernance, RACI, roadmap 2026, cadre des modules.
                    Alimente PRIsME Employé.
                  </p>
                </div>

                <div className="space-y-3">
                  <div className="flex items-center gap-4 text-sm text-rose-100">
                    <div className="flex items-center gap-2">
                      <Target className="w-4 h-4" />
                      <span>Esprit & flux PRIsME</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Users className="w-4 h-4" />
                      <span>RACI</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4" />
                      <span>Roadmap 2026</span>
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-2 text-xs">
                    <div className="bg-white/10 backdrop-blur-sm rounded-lg p-2 border border-white/20">
                      <p className="font-semibold mb-1">Esprit & Raison</p>
                      <p className="text-white/80">Vision TactiCE RH</p>
                    </div>
                    <div className="bg-white/10 backdrop-blur-sm rounded-lg p-2 border border-white/20">
                      <p className="font-semibold mb-1">Gouvernance</p>
                      <p className="text-white/80">Rôles & responsabilités</p>
                    </div>
                    <div className="bg-white/10 backdrop-blur-sm rounded-lg p-2 border border-white/20">
                      <p className="font-semibold mb-1">Cadre holistique</p>
                      <p className="text-white/80">8 modules Hub RH</p>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Cards Formation et Arbre PRIsME */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          <FormationGestionDocumentaireCard
            department="Administration & RH"
            domain="Administration & RH"
            index={0}
          />
          <ArbrePRIsMECard
            index={1}
          />
        </div>
      </div>

      <AikupModal
        item={selectedItem}
        isOpen={isModalOpen}
        onClose={handleCloseModal}
      />


      <CadreReferentielModal
        isOpen={isCadreReferentielOpen}
        onClose={() => setIsCadreReferentielOpen(false)}
      />

      <AméliorationContinueModal
        isOpen={isAméliorationOpen}
        onClose={() => setIsAméliorationOpen(false)}
      />

      <GestionRecevablesModal
        isOpen={isGestionRecevablesOpen}
        onClose={() => setIsGestionRecevablesOpen(false)}
      />

      <DemandeCreditsModal
        isOpen={isDemandeCreditsOpen}
        onClose={() => setIsDemandeCreditsOpen(false)}
      />

      <ExpertVisionModal
        isOpen={expertVisionModal.isOpen}
        onClose={() => setExpertVisionModal({ isOpen: false, expert: null, item: null })}
        expert={expertVisionModal.expert}
        item={expertVisionModal.item}
      />

      <AdminRHReunionsModal
        isOpen={isReunionsGouvernanceOpen}
        onClose={() => setIsReunionsGouvernanceOpen(false)}
      />

      <AdminRHRACIModal
        isOpen={isRACIOfficielOpen}
        onClose={() => setIsRACIOfficielOpen(false)}
      />
      <AdminRHReferentielModal
        isOpen={isReferentielAdminOpen}
        onClose={() => setIsReferentielAdminOpen(false)}
      />

      <TactiCERHModal
        isOpen={isTactiCERHModalOpen}
        onClose={() => setIsTactiCERHModalOpen(false)}
      />

      <GouvernanceContratsBZModal
        isOpen={isGouvernanceContratsOpen}
        onClose={() => setIsGouvernanceContratsOpen(false)}
      />

      <AdminRHAssurancesModal
        isOpen={isAssurancesOpen}
        onClose={() => setIsAssurancesOpen(false)}
      />

      <LitigeClientModal
        isOpen={isLitigeClientOpen}
        onClose={() => setIsLitigeClientOpen(false)}
      />

      <PolitiqueTeletravailBZModal
        isOpen={isPolitiqueTeletravailOpen}
        onClose={() => setIsPolitiqueTeletravailOpen(false)}
      />

      <PolitiqueCartesCreditComptesDepensesBZModal
        isOpen={isPolitiqueCartesCreditOpen}
        onClose={() => setIsPolitiqueCartesCreditOpen(false)}
      />

      <PolitiqueDeplacementsUSAModal
        isOpen={isPolitiqueDeplacementsUSAOpen}
        onClose={() => setIsPolitiqueDeplacementsUSAOpen(false)}
      />

      <AIInsightBubble />
    </div>
  );
}
